import {useMemo} from 'react'

import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS, INLINES} from '@contentful/rich-text-types'
import type {Document} from '@contentful/rich-text-types/dist/types/types'

import {
  Bento,
  BreakpointSize,
  Hero as PrimerHero,
  type LabelProps,
  Image,
  InlineLink,
  Link as PrimerLink,
  Text,
  type ButtonBaseProps,
  useWindowSize,
} from '@primer/react-brand'
import {Hero as PrimerHeroNext} from '@primer/react-brand-next'

import {ContentfulLogoSuite} from '@github-ui/swp-core/components/contentful/ContentfulLogoSuite'
import type {PrimerComponentLogoSuite} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerComponentLogoSuite'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import Spacer from '../../components/Spacer/Spacer'

import COPY from './Hero.data'
import imgBackground from './hero-bg.webp'

interface Link {
  href: string
  text: string
}

export interface HeroProps {
  intro: {
    label: {
      text: string
      color?: LabelProps['color']
      size?: LabelProps['size']
    }
    title: string
    ctaPrimary: Link & {variant?: ButtonBaseProps['variant']}
    ctaSecondary: Link
  }
  tertiaryCta?: {
    text?: Document
  }
  bentos: Array<{
    id: string
    title: string
    image: {
      urlDesktop: string
      urlMobile?: string
      description?: string
    }
    link: Link
  }>
  logoSuiteComponent?: PrimerComponentLogoSuite
}

const defaultProps: Partial<HeroProps> = {}

const Hero: React.FC<HeroProps> = props => {
  const initializedProps = {...defaultProps, ...props}
  const isPrimerBrandNextEnabled = isFeatureEnabled('primer_brand_next')

  const {
    intro: {
      title,
      label: {text: labelText, ...labelProps},
      ctaPrimary,
      ctaSecondary,
    },
    tertiaryCta,
    bentos,
    logoSuiteComponent,
  } = initializedProps

  const windowSize = useWindowSize()
  const isDesktopView = useMemo(
    () =>
      [BreakpointSize.MEDIUM, BreakpointSize.LARGE, BreakpointSize.XLARGE, BreakpointSize.XXLARGE].includes(
        windowSize.currentBreakpointSize!,
      ),
    [windowSize.currentBreakpointSize],
  )

  return (
    <section id="hero" className="lp-Hero">
      <div className="lp-Hero-bg">
        <Image src={imgBackground} alt="" width={1600} />
      </div>

      <div className="lp-Container">
        {isPrimerBrandNextEnabled ? (
          <PrimerHeroNext
            data-hpc
            align="center"
            className="lp-Hero-content"
            trailingComponent={
              isDesktopView && tertiaryCta?.text
                ? () => (
                    <Text weight="normal">
                      {documentToReactComponents(tertiaryCta.text!, {
                        renderNode: {
                          [BLOCKS.PARAGRAPH]: (_, children) => [children],
                          [INLINES.HYPERLINK]: (node, children) => (
                            <InlineLink
                              href={node.data.uri}
                              className="tertiary-cta"
                              style={{textDecoration: 'none'}}
                              {...getAnalyticsEvent({
                                action: documentToPlainTextString(node, ' '),
                                tag: 'link',
                                context: 'CTAs',
                                location: 'hero',
                              })}
                            >
                              {children}
                            </InlineLink>
                          ),
                        },
                      })}
                    </Text>
                  )
                : undefined
            }
          >
            <PrimerHeroNext.Label>{labelText}</PrimerHeroNext.Label>

            <PrimerHeroNext.Heading size="1">{title}</PrimerHeroNext.Heading>

            <PrimerHeroNext.PrimaryAction
              href={ctaPrimary.href}
              hasArrow={false}
              {...getAnalyticsEvent(COPY.cta1.analytics.marketingAnalyticsEvent)}
            >
              {ctaPrimary.text}
            </PrimerHeroNext.PrimaryAction>

            <PrimerHeroNext.SecondaryAction
              href={ctaSecondary.href}
              hasArrow={false}
              {...getAnalyticsEvent(COPY.cta2.analytics.marketingAnalyticsEvent)}
            >
              {ctaSecondary.text}
            </PrimerHeroNext.SecondaryAction>
          </PrimerHeroNext>
        ) : (
          <PrimerHero
            data-hpc
            align="center"
            className="lp-Hero-content"
            trailingComponent={
              isDesktopView && tertiaryCta?.text
                ? () => (
                    <Text weight="normal">
                      {documentToReactComponents(tertiaryCta.text!, {
                        renderNode: {
                          [BLOCKS.PARAGRAPH]: (_, children) => [children],
                          [INLINES.HYPERLINK]: (node, children) => (
                            <InlineLink
                              href={node.data.uri}
                              className="tertiary-cta"
                              style={{textDecoration: 'none'}}
                              {...getAnalyticsEvent({
                                action: documentToPlainTextString(node, ' '),
                                tag: 'link',
                                context: 'CTAs',
                                location: 'hero',
                              })}
                            >
                              {children}
                            </InlineLink>
                          ),
                        },
                      })}
                    </Text>
                  )
                : undefined
            }
          >
            <PrimerHero.Label {...labelProps} className="lp-Hero-label">
              {labelText}
            </PrimerHero.Label>

            <PrimerHero.Heading size="1" className="lp-Hero-heading">
              {title}
            </PrimerHero.Heading>

            <PrimerHero.PrimaryAction
              href={ctaPrimary.href}
              hasArrow={false}
              variant={ctaPrimary.variant}
              className="lp-Hero-ctaButton"
              {...getAnalyticsEvent(COPY.cta1.analytics.marketingAnalyticsEvent)}
            >
              {ctaPrimary.text}
            </PrimerHero.PrimaryAction>

            <PrimerHero.SecondaryAction
              href={ctaSecondary.href}
              hasArrow={false}
              className="lp-Hero-ctaButton"
              {...getAnalyticsEvent(COPY.cta2.analytics.marketingAnalyticsEvent)}
            >
              {ctaSecondary.text}
            </PrimerHero.SecondaryAction>
          </PrimerHero>
        )}

        <Spacer size="44px" size768="68px" />

        <Bento>
          {bentos.map((bento, index) => (
            <Bento.Item
              key={bento.id}
              columnSpan={{xsmall: 12, medium: 6}}
              visualAsBackground
              className="lp-Hero-bentoItem"
            >
              <Bento.Content>
                <Bento.Heading as="h2" size="5">
                  {bento.title}
                </Bento.Heading>
                <PrimerLink
                  variant="default"
                  href={bento.link.href}
                  {...(COPY.bentos[index]
                    ? getAnalyticsEvent(COPY.bentos[index].link.analytics.marketingAnalyticsEvent)
                    : {})}
                >
                  {bento.link.text}
                </PrimerLink>
              </Bento.Content>

              <Bento.Visual>
                <Image
                  as="picture"
                  alt={bento.image.description || ''}
                  width="600"
                  src={bento.image.urlMobile || bento.image.urlDesktop}
                  sources={[
                    {
                      srcset: bento.image.urlDesktop,
                      media: '(min-width: 544px)',
                    },
                  ]}
                />
              </Bento.Visual>
            </Bento.Item>
          ))}
        </Bento>

        <Spacer size="32px" size768="56px" />

        {logoSuiteComponent ? <ContentfulLogoSuite component={logoSuiteComponent} /> : null}
      </div>
    </section>
  )
}

export default Hero
